
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { Search, Filter, ChevronDown } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import BreederProfileCard from '@/components/BreederProfileCard';
import { BreedSelector } from '@/components/BreedSelector';
import { LocationAutocomplete } from '@/components/dashboard/LocationAutocomplete';
import { LocationRangeSlider } from '@/components/LocationRangeSlider';
import { useBreederProfiles } from '@/hooks/useBreederProfiles';

const Breeders = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBreed, setSelectedBreed] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [locationCoordinates, setLocationCoordinates] = useState<[number, number] | null>(null);
  const [rangeKm, setRangeKm] = useState(50);
  const [locationType, setLocationType] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  

  const profilesPerPage = 12;

  // Fetch breeder profiles using the hook
  const { data, isLoading, error } = useBreederProfiles({
    searchTerm,
    selectedBreed,
    selectedLocation,
    locationCoordinates,
    rangeKm,
    locationType,
    page: currentPage,
    limit: profilesPerPage
  });

  const profiles = data?.profiles || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / profilesPerPage);

  // Set initial filter state based on screen size
  useEffect(() => {
    let previousIsDesktop: boolean | null = null;
    
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024; // lg breakpoint
      
      // Only update filter state on actual desktop/mobile transitions
      if (previousIsDesktop !== null && previousIsDesktop !== desktop) {
        // Switching TO desktop: always open filters
        if (desktop) {
          setIsFiltersOpen(true);
        }
        // Switching TO mobile: keep current state (don't force close)
      } else if (previousIsDesktop === null) {
        // Initial load: close on mobile, open on desktop
        setIsFiltersOpen(desktop);
      }
      
      setIsDesktop(desktop);
      previousIsDesktop = desktop;
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedBreed, selectedLocation, locationCoordinates, rangeKm]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedBreed('');
    setSelectedLocation('');
    setLocationCoordinates(null);
    setLocationType(null);
    setRangeKm(50);
    setCurrentPage(1);
  };

  const handleLocationChange = (location: string, coordinates?: [number, number], locType?: string) => {
    setSelectedLocation(location);
    setLocationCoordinates(coordinates || null);
    setLocationType(locType || null);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <div className="lg:w-1/4">
            <div className="bg-white rounded-lg shadow-sm">
              {/* Collapsible header for mobile, always visible on desktop */}
              <Collapsible open={isFiltersOpen}>
                <div className="flex items-center justify-between w-full p-6">
                  <div className="flex items-center">
                    <Filter className="w-5 h-5 mr-2" />
                    <h2 className="text-lg font-semibold">Filters</h2>
                  </div>
                  {!isDesktop && (
                    <CollapsibleTrigger asChild>
                      <button
                        className="p-1 hover:bg-gray-100 rounded"
                        onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                        type="button"
                      >
                        <ChevronDown className="w-5 h-5" />
                      </button>
                    </CollapsibleTrigger>
                  )}
                </div>
                
                <CollapsibleContent className="lg:block">
                  <div className="px-6 pb-6 space-y-6">
                    <div data-filter-content>
                      <label className="block text-sm font-medium mb-2">Search</label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <Input
                          placeholder="Search breeders..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <div data-filter-content>
                      <label className="block text-sm font-medium mb-2">Breed</label>
                      <BreedSelector
                        value={selectedBreed}
                        onValueChange={setSelectedBreed}
                        placeholder="All breeds"
                        className="w-full"
                      />
                      {selectedBreed && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setSelectedBreed('')}
                          className="mt-1 h-6 px-2 text-xs"
                        >
                          Clear breed
                        </Button>
                      )}
                    </div>

                    <div data-filter-content>
                      <LocationAutocomplete
                        value={selectedLocation}
                        onChange={handleLocationChange}
                        placeholder="Search for a location..."
                      />
                      {selectedLocation && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleLocationChange('', undefined)}
                          className="mt-1 h-6 px-2 text-xs"
                        >
                          Clear location
                        </Button>
                      )}
                    </div>

                    {selectedLocation && locationCoordinates && (locationType === 'place' || locationType === 'locality') && (
                      <div>
                        <LocationRangeSlider
                          value={rangeKm}
                          onChange={setRangeKm}
                        />
                      </div>
                    )}

                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={clearFilters}
                    >
                      Clear All Filters
                    </Button>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </div>

          {/* Profiles Section */}
          <div className="lg:w-3/4">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Breeders</h1>
              <p className="text-gray-600">{totalCount} breeders found</p>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Unable to load breeders at the moment.</p>
              </div>
            ) : profiles.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">No breeders found matching your criteria.</p>
              </div>
            ) : (
              <>
                <div className="space-y-6">
                  {profiles.map((profile) => (
                    <BreederProfileCard key={profile.id} profile={profile} />
                  ))}
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8 flex justify-center">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious 
                            onClick={() => handlePageChange(currentPage - 1)}
                            className={currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                          />
                        </PaginationItem>
                        
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <PaginationItem key={pageNum}>
                              <PaginationLink
                                onClick={() => handlePageChange(pageNum)}
                                isActive={currentPage === pageNum}
                                className="cursor-pointer"
                              >
                                {pageNum}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        })}
                        
                        <PaginationItem>
                          <PaginationNext 
                            onClick={() => handlePageChange(currentPage + 1)}
                            className={currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Breeders;
